/**
 * @author      carlos.soto@zumper.com (Carlos J. Soto)
 * @copyright   Copyright (c) 2022, Zumper
 * @description ListItem - Short Term View
 */

/** @jsx jsx */
import { jsx } from '@emotion/react'
import { ClearButton, SecondaryLinkButton } from '@zcode-ui/controls'
import { selectShowNightlyPrice } from '@zcode-ui/filters'
import { Close, ExternalLink } from '@zcode-ui/icons'
import { getListCardAmenities } from '@zcode-ui/web-zumper-features'
import { getDetailHref } from '@zcode-ui/web-zumper-global-state'
import { PropertyFeatures } from '@zcode/enums'
import { addTrackingToUrl } from '@zcode/nr-utils'
import { a11yLinkHandler } from '@zcode/nr/es/shared/utils/a11y'
import { voidEvent } from '@zcode/nr/es/shared/utils/utils'
import type { Listable } from '@zcode/state-type'
import { capitalize, rootRelative } from '@zcode/utils'
import { Fragment, type KeyboardEvent, type MouseEvent } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import type { Dispatch } from 'redux'
import { mixpanelShortTermDetailClick } from '../../../../../../modules/mixpanel/thunks/mixpanelShortTermDetailClick'
import { ListCardRating } from '../../../../../detail/RatingsAndReviews/ListCardRating/ListCardRating'
import { getFeedName } from '../../../../../detail/helpers/getFeedName'
import * as styles from './styles'

interface ContentWrapperProps {
  bookingProviderHref: string
  durationTextTestID?: string
  entity: Listable
  entityTitleTestID?: string
  guestCapacityTestID?: string
  index: number
  isInitialLocationChanged: boolean
  onClick: (event: KeyboardEvent<HTMLElement> | MouseEvent<HTMLElement>) => void
  onClickMoreAmenities?: () => void
  onClickout: () => void
  page: number
  priceTextTestID?: string
  setShowInfo: React.Dispatch<React.SetStateAction<boolean>>
  showClickOutCTA: boolean
  showInfo: boolean
  stayLength?: number
}

export const ContentWrapper = ({
  bookingProviderHref,
  durationTextTestID,
  entity,
  entityTitleTestID,
  guestCapacityTestID,
  index,
  isInitialLocationChanged,
  onClick,
  onClickMoreAmenities,
  onClickout,
  page,
  priceTextTestID,
  setShowInfo,
  showClickOutCTA,
  showInfo,
  stayLength,
}: ContentWrapperProps) => {
  const {
    bedroomAbbrTxtStr,
    bedsBathsTxt,
    buildingName,
    city,
    features,
    isBuilding,
    isShortTerm,
    isUnavailable,
    maxAdults: guests,
    headerText,
    minLeaseDays,
    minPriceText,
    neighborhoodName,
    pricePerNight,
    priceTxt,
    rating,
    title,
    propertyTypeText,
    providerUrl,
    url,
  } = entity

  const dispatch = useDispatch<Dispatch>()
  const shouldShowNightlyPrice = useSelector(selectShowNightlyPrice)
  const amenities = entity.combinedAmenities
  const { amenitiesList, amenitiesBlurb, otherSpacesBlurb } = getListCardAmenities(amenities)

  const location = neighborhoodName ?? city

  const locationString = String(location)
  const showPricePerNight = shouldShowNightlyPrice && !!pricePerNight

  const showMinStayBlurb =
    shouldShowNightlyPrice && !showClickOutCTA && !!minLeaseDays && isShortTerm
  const providerURL = bookingProviderHref || url

  const onClickClose = (e: MouseEvent<HTMLElement>) => {
    voidEvent(e)
    setShowInfo(false)
  }
  const onClickMore = (e: MouseEvent<HTMLElement>) => {
    voidEvent(e)
    setShowInfo(true)
    onClickMoreAmenities?.()
  }

  const handleClickout = (e: MouseEvent<HTMLElement> | undefined) => {
    e?.stopPropagation()
    if (providerUrl) {
      onClickout()
    }
  }

  const handleDetailClick = (e: MouseEvent<HTMLElement>) => {
    e?.stopPropagation()
    isShortTerm && dispatch(mixpanelShortTermDetailClick({ entity, index, page }))
    onClick(e)
  }

  const hasNoPrice = priceTxt === 'N/A' && isUnavailable
  const href = rootRelative(providerURL)
  const feedName = getFeedName(entity)
  const detailHref = dispatch(addTrackingToUrl(getDetailHref(entity)))
  const interstitialHref = `/provider-interstitial?provider=${feedName}&url=${href.replaceAll(
    '&',
    '$'
  )}&preview`

  let priceText
  if (hasNoPrice) {
    priceText = '$ -'
  } else if (priceTxt === 'N/A' && features?.includes(PropertyFeatures.USES_WAITLIST)) {
    priceText = '$ - Call for pricing'
  } else {
    priceText = priceTxt
  }

  const defaultHeaderText = title || headerText
  const header = isBuilding ? buildingName || defaultHeaderText : defaultHeaderText

  return (
    <Fragment>
      <div css={[styles.mainContent, showInfo && styles.hideContent]}>
        <div css={styles.mainSection}>
          <div css={styles.topRow}>
            <div css={styles.propertyName} data-testid={entityTitleTestID}>
              {header}
            </div>
            <div css={[styles.typeAndLocation, styles.gridTypeAndLocation]}>
              <ListCardRating rating={rating} />
              <div className="h-5 px-1">&#183;</div>
              {showClickOutCTA && (
                <ClearButton
                  css={styles.detailBtn2}
                  onClick={handleDetailClick}
                  onKeyPress={a11yLinkHandler(onClick)}
                >
                  Details
                </ClearButton>
              )}
            </div>
            <div css={styles.bedBathText}>
              <div className="overflow-hidden text-ellipsis whitespace-nowrap">
                {locationString}
              </div>
              <div className="h-5 px-1">&#183;</div>
              <div className="whitespace-nowrap">{bedroomAbbrTxtStr}</div>
              <div className="h-5 px-1">&#183;</div>
              {guests && (
                <div className="flex">
                  <div className="whitespace-nowrap" data-testid={guestCapacityTestID}>
                    Sleeps {guests}
                  </div>
                  <div className="h-5 px-1">&#183;</div>
                </div>
              )}
              <span>
                <button css={styles.moreLink} onClick={onClickMore}>
                  More
                </button>
              </span>
            </div>
            <div css={[styles.amenitiesText, styles.hideContent]}>
              {amenitiesBlurb && `${amenitiesBlurb} · `}
              <button css={styles.moreLink} onClick={onClickMore}>
                More
              </button>
            </div>
          </div>
        </div>

        <div css={styles.priceAndLogo}>
          <div css={[styles.priceContainer, styles.gridPriceContainer]}>
            {isShortTerm ? (
              <div>
                <div css={styles.rateHeader}>From</div>
                <span css={[styles.rateText]} data-testid={priceTextTestID}>
                  {showPricePerNight ? pricePerNight : minPriceText}
                </span>
                <span css={styles.durationText} data-testid={durationTextTestID}>
                  {showPricePerNight ? '/night' : '/month'}
                </span>
              </div>
            ) : (
              <div>
                <div css={styles.spacing} />
                <span css={[styles.rateText]} data-testid={priceTextTestID}>
                  {priceText}
                </span>
                <span css={styles.durationText} data-testid={durationTextTestID}>
                  /month
                </span>
              </div>
            )}
          </div>
          <Fragment>
            {showClickOutCTA ? (
              <SecondaryLinkButton
                css={styles.clickOutCTA(Boolean(stayLength))}
                href={isInitialLocationChanged ? interstitialHref : detailHref}
                onClick={handleClickout}
                rel="nofollow noopener"
                size="sm"
                target="_blank"
                useButtonStyles
              >
                <ExternalLink /> <span>{stayLength ? 'View deal' : 'Check availability'}</span>
              </SecondaryLinkButton>
            ) : (
              <Fragment>
                {showMinStayBlurb && (
                  <div css={[styles.logoContainer, styles.minStayBlurb]}>
                    Min stay: {minLeaseDays} nights
                  </div>
                )}
              </Fragment>
            )}
          </Fragment>
        </div>
      </div>
      <div css={[styles.extraContent, !showInfo && styles.hideContent]}>
        <div css={styles.closeButtonWrapper}>
          <ClearButton onClick={onClickClose}>
            <Close size={10} />
          </ClearButton>
        </div>
        <div css={styles.amenitiesSection}>
          <div>
            <div css={styles.amenitiesHeader}>Property</div>
            <div css={styles.otherSpacesText}>
              {capitalize(propertyTypeText)} · {bedsBathsTxt}
            </div>
          </div>
          {amenitiesList && amenitiesList.length > 0 && (
            <div>
              <div css={styles.amenitiesHeader}>Amenities</div>
              <div css={styles.amenitiesList}>
                {amenitiesList.map((item, idx) => {
                  return (
                    item &&
                    idx < 8 && (
                      <div css={styles.amenitiesItem} key={`${idx}-${item}`}>
                        {item}
                      </div>
                    )
                  )
                })}
              </div>
            </div>
          )}
          {otherSpacesBlurb && (
            <div>
              <div css={styles.amenitiesHeader}>Other spaces</div>
              <div css={styles.otherSpacesText}>{otherSpacesBlurb}</div>
            </div>
          )}
          {/* <div>
            <strong>Cancellation policy:</strong> Full refund up to 14 days before check-in
          </div> */}
        </div>
      </div>
    </Fragment>
  )
}
